import { Box, Input, Typography, Button, IconButton, Stack } from "@mui/joy";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import { TrendingUp, Plus, X, Square } from "lucide-react";

interface IRoofSlopes {
    value: string | null;
    onChange: (value: string) => void;
    loading?: boolean;
}

interface SlopeEntry {
    section: string;
    pitch: string;
    squares: string;
}

/**
 * Multi-slope entry component.
 * Stores as JSON array: [{"section":"Main","pitch":"6/12","squares":"12"},{"section":"Garage","pitch":"4/12","squares":"4"}]
 */
export function RoofSlopes(props: IRoofSlopes) {
    const { value, onChange, loading } = props;

    const parseValue = (val: string | null): SlopeEntry[] => {
        if (!val) return [{ section: "", pitch: "", squares: "" }];
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length > 0) {
                // Migrate old entries that don't have squares
                return parsed.map((e: any) => ({
                    section: e.section || "",
                    pitch: e.pitch || "",
                    squares: e.squares !== undefined ? String(e.squares) : "",
                }));
            }
        } catch {
            // Legacy single value — migrate it
            if (val) return [{ section: "Main", pitch: val, squares: "" }];
        }
        return [{ section: "", pitch: "", squares: "" }];
    };

    const [slopes, setSlopes] = useState<SlopeEntry[]>(() => parseValue(value));

    const debouncedOnChange = useRef(
        debounce((entries: SlopeEntry[]) => {
            const filtered = entries.filter((e) => e.pitch.trim() !== "");
            if (filtered.length > 0) {
                onChange(JSON.stringify(filtered));
            }
        }, 500),
    ).current;

    useEffect(() => {
        return () => {
            debouncedOnChange.cancel();
        };
    }, [debouncedOnChange]);

    useEffect(() => {
        if (value) {
            setSlopes(parseValue(value));
        }
    }, [value]);

    const handleUpdate = (index: number, field: keyof SlopeEntry, val: string) => {
        if (loading) return;
        const updated = slopes.map((s, i) => (i === index ? { ...s, [field]: val } : s));
        setSlopes(updated);
        debouncedOnChange(updated);
    };

    const addSlope = () => {
        setSlopes((prev) => [...prev, { section: "", pitch: "", squares: "" }]);
    };

    const removeSlope = (index: number) => {
        if (slopes.length <= 1) return;
        const updated = slopes.filter((_, i) => i !== index);
        setSlopes(updated);
        debouncedOnChange(updated);
    };

    return (
        <Box flex={1}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>
                Roof Slopes
            </Typography>
            <Stack spacing={1}>
                {slopes.map((entry, i) => (
                    <Stack direction="row" spacing={1} key={i} alignItems="flex-start">
                        {/* Section name */}
                        <Input
                            size="sm"
                            placeholder="Section (e.g. Main)"
                            value={entry.section}
                            onChange={(e) => handleUpdate(i, "section", e.target.value)}
                            disabled={loading}
                            sx={{ flex: 1.5 }}
                        />
                        {/* Pitch */}
                        <Input
                            size="sm"
                            placeholder="Pitch (e.g. 6/12)"
                            value={entry.pitch}
                            onChange={(e) => handleUpdate(i, "pitch", e.target.value)}
                            disabled={loading}
                            startDecorator={<TrendingUp size={14} />}
                            sx={{ flex: 0.8 }}
                        />
                        {/* Squares */}
                        <Input
                            size="sm"
                            type="number"
                            placeholder="SQ"
                            value={entry.squares}
                            onChange={(e) => handleUpdate(i, "squares", e.target.value)}
                            disabled={loading}
                            startDecorator={<Square size={14} />}
                            endDecorator={
                                <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: "bold" }}>
                                    SQ
                                </Typography>
                            }
                            slotProps={{ input: { min: 0, step: 0.1 } }}
                            sx={{ flex: 0.7 }}
                        />
                        {slopes.length > 1 && (
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="danger"
                                onClick={() => removeSlope(i)}
                                sx={{ "--IconButton-size": "28px", border: "1px solid", borderColor: "divider" }}
                            >
                                <X size={14} />
                            </IconButton>
                        )}
                    </Stack>
                ))}
                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={14} />}
                    onClick={addSlope}
                    sx={{ alignSelf: "flex-start", mt: "12px !important" }}
                >
                    Add Slope
                </Button>
            </Stack>
        </Box>
    );
}
